import React, { useMemo, useState } from 'react'
import { usePro } from '../../hooks'
import { formatPrizeTypeLabel, INCENTIVE_BASIS_LABEL } from '../../utils/incentiveDisplay'
import styles from './ModalProProfile.module.scss'

const CAREER_LEVEL_LABELS = {
	NEWBIE: '신입',
	ONE_TO_THREE: '1~3년차',
	FOUR_TO_SIX: '4~6년차',
	SEVEN_TO_TEN: '7~10년차',
	OVER_TEN: '10년 이상',
}

export const ModalProProfile = ({ modalVisible, onClose, user, ...props }) => {
	const [activeContractTab, setActiveContractTab] = useState('domestic')

	// 프로 디테일 정보 조회
	const { data: proDetail, isLoading, error } = usePro(user?.id, modalVisible)

	// 서버 응답 구조: user_player_contract는 user 객체 안에 있음
	const preferred = proDetail?.user?.user_player_contract?.[0]
	const domestic = preferred?.domesticContract
	const international = preferred?.overseasContract

	// 포맷팅 함수들
	const formatTourType = (type) => {
		if (type === 'KPGA') return 'KPGA'
		if (type === 'KLPGA') return 'KLPGA'
		return type || '-'
	}

	const formatCurrency = (amount) => {
		if (amount === null || amount === undefined) return '-'
		try {
			return `${Number(amount).toLocaleString('ko-KR')}원`
		} catch (_) {
			return `${amount}`
		}
	}

	const formatYesNo = (val) => (val ? '가능' : '불가능')

	const formatPayroll = (cycle) => {
		switch (cycle) {
			case 'weekly':
				return '주급'
			case 'monthly':
				return '월급'
			default:
				return cycle || '-'
		}
	}

	const formatTaxIncluded = (included) => {
		if (included === undefined || included === null) return '-'
		return included ? '세후' : '세전'
	}

	const formatIncentiveRange = (startRank, endRank) => {
		if (startRank === 1 && endRank === 1) return '우승'
		const endLabel = endRank === 0 ? 'CUT' : endRank
		return `${startRank} ~ ${endLabel}`
	}

	// 헤더용 데이터 (user prop 우선, proDetail로 보강)
	const headerName = proDetail?.user?.name || user?.name || '-'
	const headerTourType = formatTourType(proDetail?.tour_type || user?.user_tour_pro?.tour_type)
	const headerCareerLevel =
		CAREER_LEVEL_LABELS[proDetail?.career_level || user?.user_tour_pro?.career_level] || null
	const headerImage =
		proDetail?.user?.profile_image || user?.profile_image || '/images/pro-default.png'
	const headerSponsor =
		proDetail?.main_sponsor?.name ||
		proDetail?.main_sponsor ||
		user?.user_tour_pro?.main_sponsor?.name ||
		user?.user_tour_pro?.main_sponsor ||
		null
	const headerAgency =
		proDetail?.agency?.name ||
		proDetail?.agency ||
		user?.user_tour_pro?.agency?.name ||
		user?.user_tour_pro?.agency_name ||
		null

	// 국내 계약 Key-Value
	const domesticKeyValues = useMemo(() => {
		if (!domestic) return []
		return [
			{ key: '세금 포함 여부', value: formatTaxIncluded(domestic.isSalaryTaxIncluded) },
			{ key: '임금 지급 주기', value: formatPayroll(domestic.payrollCycles) },
			{ key: '컷 통과', value: formatCurrency(domestic.weeklySalaryCutThrough) },
			{ key: '컷 아웃', value: formatCurrency(domestic.weeklySalaryCutFail) },
			{ key: '제주 대회 참여', value: formatYesNo(domestic.jejuTournamentParticipation) },
			{ key: '제주 지원금', value: formatCurrency(domestic.expenseReimbursementJeju) },
			{ key: INCENTIVE_BASIS_LABEL, value: formatPrizeTypeLabel(domestic?.prizeType) },
			{
				key: '인센티브 금액',
				value:
					domestic?.incentives?.length > 0
						? domestic.incentives
								.map(
									(incentive) =>
										`${formatIncentiveRange(incentive.startRank, incentive.endRank)} : ${incentive.percentage}%`,
								)
								.join('\n')
						: '-',
			},
			{ key: '스폰서십 모자', value: formatYesNo(domestic.wearSponsoredHat) },
		]
	}, [domestic])

	// 해외 계약 Key-Value
	const internationalKeyValues = useMemo(() => {
		if (!international) return []
		return [
			{ key: '세금 포함 여부', value: formatTaxIncluded(international.isSalaryTaxIncluded) },
			{ key: '임금 지급 주기', value: formatPayroll(international.payrollCycles) },
			{ key: '컷 통과', value: formatCurrency(international.weeklySalaryCutThrough) },
			{ key: '컷 아웃', value: formatCurrency(international.weeklySalaryCutFail) },
			{ key: INCENTIVE_BASIS_LABEL, value: formatPrizeTypeLabel(international?.prizeType) },
			{
				key: '인센티브 금액',
				value:
					international?.incentives?.length > 0
						? international.incentives
								.map(
									(incentive) =>
										`${formatIncentiveRange(incentive.startRank, incentive.endRank)} : ${incentive.percentage}%`,
								)
								.join('\n')
						: '-',
			},
			{ key: '스폰서십 모자', value: formatYesNo(international.wearSponsoredHat) },
			{ key: '해외 지원금', value: formatCurrency(international.expenseReimbursement) },
		]
	}, [international])

	// 프로 정보 Key-Value
	const proKeyValues = useMemo(() => {
		if (!proDetail) return []
		return [
			{ key: '소속협회', value: formatTourType(proDetail.tour_type) },
			{ key: '회원번호', value: proDetail.full_membership_number || '-' },
			{
				key: '경력',
				value: CAREER_LEVEL_LABELS[proDetail.career_level] || proDetail.career_level || '-',
			},
			{
				key: '메인스폰서',
				value: proDetail.main_sponsor?.name || proDetail.main_sponsor || '-',
			},
			{ key: '에이전시', value: proDetail.agency?.name || proDetail.agency || '-' },
			{ key: 'TOP 10', value: proDetail.top10_count ? `${proDetail.top10_count}회` : '-' },
			{
				key: '컷 통과율',
				value: proDetail.cut_through_rate ? `${proDetail.cut_through_rate}%` : '-',
			},
		]
	}, [proDetail])

	if (!modalVisible) return null

	return (
		<div className={styles.overlay} onClick={onClose}>
			<div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
				<div className={styles.modalScrollBody}>
					{/* ── 헤더: 큰 프로필 이미지 + 오버레이 정보 ── */}
					<div
						className={styles.modalPhotoHeader}
						style={{ backgroundImage: `url(${headerImage})` }}>
						{headerCareerLevel && (
							<span className={styles.modalHeaderCareerBadge}>
								{headerCareerLevel}
							</span>
						)}
						{headerTourType && headerTourType !== '-' && (
							<span className={styles.modalHeaderTypeBadge}>{headerTourType}</span>
						)}
						<div className={styles.modalPhotoOverlay} />
						<div className={styles.modalInfoOverlay}>
							<span className={styles.modalHeaderName}>{headerName} 프로</span>
							{(headerSponsor || headerAgency) && (
								<span className={styles.modalHeaderMeta}>
									{[headerSponsor, headerAgency].filter(Boolean).join(' · ')}
								</span>
							)}
						</div>
					</div>

					{/* ── 탭 컨텐츠 ── */}
					<div className={styles.tabContent}>
						<div className={styles.profileContent}>
							{/* 로딩 */}
							{isLoading && (
								<div className={styles.loadingMessage}>불러오는 중...</div>
							)}

							{/* 선호 계약 정보 */}
							{!isLoading && (
								<div className={styles.profileSection}>
									<h3 className={styles.sectionTitle}>선호 계약 정보</h3>
									{domestic && international && (
										<div className={styles.contractTabRow}>
											<div
												className={`${styles.tabItem} ${activeContractTab === 'domestic' ? styles.active : ''}`}
												onClick={() => setActiveContractTab('domestic')}>
												국내
											</div>
											<div
												className={`${styles.tabItem} ${activeContractTab === 'international' ? styles.active : ''}`}
												onClick={() =>
													setActiveContractTab('international')
												}>
												해외
											</div>
										</div>
									)}
									<div className={styles.keyValueList}>
										{!domestic && !international ? (
											<div className={styles.emptyMessage}>
												선호 계약 정보가 없습니다.
											</div>
										) : (
											<>
												{((domestic &&
													international &&
													activeContractTab === 'domestic') ||
													(domestic && !international)) &&
													domesticKeyValues.map((row) => (
														<div
															key={row.key}
															className={styles.keyValueRow}>
															<span className={styles.kvKey}>
																{row.key}
															</span>
															<span className={styles.kvValue}>
																{row.value}
															</span>
														</div>
													))}
												{((domestic &&
													international &&
													activeContractTab === 'international') ||
													(!domestic && international)) &&
													internationalKeyValues.map((row) => (
														<div
															key={row.key}
															className={styles.keyValueRow}>
															<span className={styles.kvKey}>
																{row.key}
															</span>
															<span className={styles.kvValue}>
																{row.value}
															</span>
														</div>
													))}
											</>
										)}
									</div>
								</div>
							)}

							{/* 프로 정보 */}
							{!isLoading && proKeyValues.length > 0 && (
								<div
									className={styles.profileSection}
									style={{ marginTop: '12px' }}>
									<h3 className={styles.sectionTitle}>프로 정보</h3>
									<div className={styles.keyValueList}>
										{proKeyValues.map((row) => (
											<div key={row.key} className={styles.keyValueRow}>
												<span className={styles.kvKey}>{row.key}</span>
												<span className={styles.kvValue}>{row.value}</span>
											</div>
										))}
									</div>
								</div>
							)}
						</div>
					</div>
				</div>

				{/* 닫기 버튼 - 하단 고정 */}
				<div className={styles.modalFooter}>
					<button onClick={onClose} className={styles.closeButton}>
						닫기
					</button>
				</div>
			</div>
		</div>
	)
}
